import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import {
  ScrollText,
  BookOpenText,
  NotebookPen,
  Battery,
  BatteryCharging,
  BadgeIndianRupee,
  BadgeCheck,
  Bell,
  Boxes,
  Camera,
  Building2,
  ChevronsLeft,
  ChevronsRight,
  ClipboardCheck,
  Cog,
  ClipboardList,
  FileBarChart,
  Gauge,
  Hourglass,
  Landmark,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  ArrowLeft,
  Menu,
  Monitor,
  Moon,
  PackageCheck,
  Paintbrush,
  ArrowDownToLine,
  ArrowUpFromLine,
  ReceiptText,
  RefreshCw,
  Search,
  ChevronDown,
  X,
  Send,
  Settings,
  Store,
  Sun,
  TrendingUp,
  Truck,
  Undo2,
  UserPlus,
  Handshake,
  ShieldCheck,
  Users,
  Warehouse,
  FileSpreadsheet,
  ShoppingCart,
  Factory,
  Activity as ActivityIcon,
  Megaphone,
  ListChecks,
  PhoneCall,
  CalendarCheck,
  Headset,
  ReceiptIndianRupee,
  Wallet,
  ArrowLeftRight,
  Inbox,
  UserRoundCheck,
  Scale,
  Wrench,
  MessagesSquare,
} from "lucide-react";
import { BILLING_DESK_ROLES, chatsInCompany, claimsExpenses, givesTasks, hasApprovalsSection, isAsmRole, isCeoDesk, isGuardRole, isHeadAdminDesk, isStaffRole, isTelecaller, isVerifiedEmployee, keepsDayBook, readsCeoDesk, readsEveryEnquiry, reconcilesExpenses, worksDealerOnboarding, financeReceiptBooks, seesSwaps, transfersOpenTo, type PermissionKey, type Paginated, type RoleKey } from "@bright/shared";
import { api } from "@/lib/api";
import { useFernlyLook, wearsFernly } from "@/lib/fernly";
import { Backstep } from "./Backstep";
import { useToast } from "./ui/Toast";
import { VerificationNudge } from "@/features/verification/VerificationNudge";
import { TasksNudge } from "@/features/tasks/TasksNudge";
import { AttendanceNudge } from "@/features/attendance/AttendanceNudge";
import { EnquiryNudge } from "@/features/enquiries/EnquiryNudge";
import { useAttendanceRole } from "@/features/attendance/attendance-api";
import { useLeadRole } from "@/features/leads/leads-api";
import { LeadsNudge } from "@/features/leads/LeadsNudge";
import { taskBadgeCount, useTaskRole, useTaskSummary } from "@/features/tasks/tasks-api";
import { useApprovals, useApprovesFactory } from "@/features/factory/control-api";
import { ApprovalsNudge } from "@/features/approvals/ApprovalsNudge";
import { CoverNudge } from "@/features/covers/CoverNudge";
import { useReadsShowrooms } from "@/features/showrooms/showrooms-api";
import { useApprovalsSummary } from "@/features/approvals/approvals-api";
import { useChatUnread } from "@/features/chat/chat-api";
import { isStoreDesk } from '@/lib/roles';
import { useAuth, type AuthUser } from "@/lib/auth";
import { actingAs } from "@/lib/cover";
import { useActiveOrg, useBookIdentity } from "@/lib/active-org";
import { BOOK_LABEL, bookKeyFor, booksFor, checkableBooks, coverInUse, setActiveBook, useActiveBook } from "@/lib/book";
import { useTheme } from "@/lib/theme";
import { UpdateBanner } from '@/components/UpdateBanner';
import {
  Badge,
  Button,
  Drawer,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  Popover,
  Tooltip,
} from "./ui";
import { IncomingTransferBanner } from '@/features/transfers/IncomingTransfers';

/**
 * The frame.
 *
 * A fixed rail of sections on the left, a thin instrument bar across the top, and the
 * page in the middle. The rail collapses to icons for someone who lives in the
 * product and knows the order by heart, and becomes a drawer below 1024px. Nothing in
 * here competes with the page: the shell is furniture.
 */

/**
 * The shell asks for the command palette by shouting into the window rather than by
 * importing it. The palette is mounted at the application root and the shell is
 * mounted inside the router below it, so a direct call would mean a context that
 * wraps the router or an import cycle. One event is cheaper than either.
 */
export const COMMAND_PALETTE_EVENT = "bsa:command-palette";

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  permission: PermissionKey;
  /** Matches child routes as well — /dealers/:id keeps Dealers marked. */
  section?: string;
  end?: boolean;
  /** Reads the whole book with no territory filter — an ASM session is refused there. */
  wholeBook?: boolean;
  /**
   * Only in a distributor's book. The billing rail is shared by the maker's
   * billing desk and each distributor's, and "Dispatch from Autozone" means
   * nothing in Autozone's own book — it is the other side of the same page.
   */
  distributorOnly?: boolean;
  /**
   * Only in the maker's book (owner, 18 Sep 2026: "remove dispatched from
   * bright on wheels it should be in autozone"). The dispatched register is
   * the sending side; a distributor's book has "Dispatch from Autozone".
   */
  wholesaleOnly?: boolean;
  /**
   * Only at the STORE counter — the sign-in scoped to a DISTRIBUTOR party.
   * The enquiry book is a showroom thing; a mitr in its own shop has no use
   * for it (owner, 15 Sep 2026: "limit the enquiry form to vivek only").
   */
  storeOnly?: boolean;
  /**
   * Only for an area sales manager of either rank (owner, 1 Oct 2026: "add
   * enquiry form in every asm as well"). The head ASMs ride the main rail, so
   * the main rail carries their enquiry book under this flag.
   */
  asmOnly?: boolean;
  /**
   * The enquiry book on the main rail: every ASM's, and the owner's — who
   * alone reads all of it (13 Oct 2026). The rest of the office does not keep one.
   */
  enquiryBook?: boolean;
  /**
   * Only in the TVS book (owner, 23 Sep 2026: "on tvs book add a option of
   * manually adding busy data"). BUSY's TVS files have no meaning in the
   * other books, and the door refuses them there.
   */
  tvsOnly?: boolean;
  /**
   * Only in an Autozone partner book. "Dispatch from Autozone" is the other
   * side of the maker's dispatch; the TVS book buys from TVS and has none.
   */
  partnerOnly?: boolean;
  /**
   * The Bright on Wheels ledger (owner, 28 Sep 2026): in that book, and in the
   * maker's — where the owner and Neeraj sign in and read it — never in TVS or
   * FAB EV, whose sales it does not hold. A counter sees it in its own book only.
   */
  ledgerOnly?: boolean;
}

interface NavGroup {
  key: string;
  label: string;
  items: NavItem[];
}

/**
 * The rail an area sales manager sees (owner, 8 Sep 2026: "for an ASM it
 * should list their mitras, their performance analysis, their stock and
 * ageing — that's it"), plus the orders they take on calls. Every destination
 * is pinned to their territory server-side.
 */
const ASM_NAV: NavGroup[] = [
  {
    key: "territory",
    /*
     * A territory ASM gets their mitra stock and their orders — place one,
     * watch its status — and nothing else (owner, 17 Sep 2026: "except for
     * manish and goswami other ASM will be shown their mitra stock only and
     * will be able to place orders and check the status of their orders thats
     * it"). Mitras, ageing and performance are the head ASMs' — the sign-ins
     * with no territory, which land on the whole book instead.
     */
    label: "Your territory",
    items: [
      {
        to: "/",
        label: "Home",
        icon: <LayoutDashboard />,
        permission: "vehicles:read",
        end: true,
      },
      {
        to: "/stock?status=AT_SUB_DEALER",
        label: "Their stock",
        icon: <Warehouse />,
        permission: "vehicles:read",
      },
      // Their mitrs' and dealers' sales, month by month (owner, 13 Oct 2026:
      // "add a sales performance section in each asm"). Held to their own server-side.
      { to: "/sales-performance", label: "Sales performance", icon: <TrendingUp />, permission: "analytics:read" },
      {
        to: "/orders",
        label: "Orders",
        icon: <ClipboardList />,
        permission: "vehicles:read",
      },
      // Who asked in the field — a dealer, a mitra or an outlet to be (1 Oct 2026) — and, since 14 Oct 2026,
      // the meetings held with them ("log a meeting shoul be in enquiry"). Expenses are in the Work group.
      // Enquiries and meetings are one desk (27 Oct 2026): the meeting is logged on the enquiry.
      { to: "/enquiries", label: "Enquiries & meetings", icon: <UserPlus />, permission: "vehicles:read" },
    ],
  },
];

/** The rail a buyer portal account sees. Every destination is scoped server-side. */
const BUYER_NAV: NavGroup[] = [
  {
    key: "buyer",
    label: "Your desk",
    items: [
      {
        to: "/",
        label: "My stock",
        icon: <Warehouse />,
        permission: "vehicles:read",
        end: true,
      },
      {
        to: "/enquiries",
        label: "Enquiries",
        icon: <UserPlus />,
        permission: "vehicles:sell",
        storeOnly: true,
      },
      {
        to: "/my-sales",
        label: "My sales",
        icon: <TrendingUp />,
        permission: "vehicles:read",
      },
      // Sold vehicles with their bill, insurance and papers — open and download (owner, 21 Oct 2026).
      {
        to: "/sold-vehicles",
        label: "Sold vehicles",
        icon: <ReceiptText />,
        permission: "vehicles:read",
      },
      // What this counter owes on its sales, and the receipts still to give (28 Sep 2026).
      { to: "/ledger", label: "Ledger", icon: <BookOpenText />, permission: "ledger:read", ledgerOnly: true },
      // The TVS quick sale, for now (23 Sep 2026): a counter's own stock, one press.
      {
        to: "/tvs-sale",
        label: "Sale",
        icon: <ShoppingCart />,
        permission: "vehicles:quick-sell",
        tvsOnly: true,
      },
      {
        to: "/search",
        label: "Search",
        icon: <Search />,
        permission: "vehicles:read",
      },
    ],
  },
  // Service and orders as their own section of the rail (owner, 8 Sep 2026:
  // "bring service and orders into the sidebar, section-wise, in the outlet
  // and mitr portal"). Each opens the desk on its own kind.
  {
    key: "requests",
    label: "Service & orders",
    items: [
      {
        to: "/service",
        label: "Service",
        icon: <LifeBuoy />,
        permission: "vehicles:read",
      },
      {
        to: "/my-orders",
        label: "Orders",
        icon: <ClipboardList />,
        permission: "vehicles:read",
      },
    ],
  },
];

/**
 * The rail for the OEM book's staff. Wholesale has no outlets, mitrs or
 * financiers — its nouns are dispatches and buyers — and its home is the sales
 * board, because an OEM holds no stock.
 */
const WHOLESALE_NAV: NavGroup[] = [
  {
    key: "overview",
    label: "Overview",
    items: [
      {
        /*
         * "Dashboard", not "Sales" (owner, 19–20 Sep 2026, twice: "there is no
         * dedicated dashboard on the bright autozone", then "still cant see the
         * dashboard section").
         *
         * The page had been called Sales since the OEM board was built, back
         * when it only reported what the maker billed out. It now reads the
         * whole line — what the network holds, what it sold, what it is asking
         * for — and the owner came looking for it by the word every other book
         * uses for its home. The rail said Sales and the page said "The line",
         * so the word he was hunting for was nowhere on the screen.
         */
        to: "/",
        label: "Dashboard",
        icon: <LayoutDashboard />,
        permission: "analytics:read",
        end: true,
      },
      // Who is using the app, and the work each person did in it (owner, 29 Sep 2026:
      // "i want an activity monitor in the app bright autozone").
      { to: "/activity", label: "Activity", icon: <ActivityIcon />, permission: "activity:read" },
      // The ASMs' month in the field, and the review and incentive (owner, 3 Oct 2026).
      { to: "/field-team", label: "Field team", icon: <Handshake />, permission: "field:review" },
      // Each ASM's mitrs and dealers, sale by sale (owner, 13 Oct 2026) — the maker's
      // home is its own, so the rail carries it here as well as on NAV.
      { to: "/sales-performance", label: "ASM sales", icon: <TrendingUp />, permission: "analytics:read" },
    ],
  },
  {
    key: "book",
    label: "The book",
    items: [
      /*
       * Autozone's two stores, in the order a vehicle passes through them
       * (owner, 25 Sep 2026: "show the pdi store and dispatch store as well in
       * bright autozone"). The factory's chassis wait in the PDI store until
       * the yard checks them; the dispatch store is what may go out. Only the
       * dispatch desk's rail carried the first, and this one called the second
       * plain "Store" — so the owner, looking at the Production page's "in the
       * store" and "waiting for PDI", had no door to either by those names.
       */
      // The factory register (28 Sep 2026: "create a new coloumn of the purchase
      // data"), first: in the rail as on the floor, a chassis is made, checked, sent.
      { to: "/purchase-data", label: "Purchase data", icon: <ScrollText />, permission: "vehicles:read" },
      { to: "/pdi", label: "Production store", icon: <ClipboardCheck />, permission: "vehicles:read" },
      { to: "/store", label: "HEAD OFFICE", icon: <Warehouse />, permission: "vehicles:read" },
      /*
       * Autozone sells to dealers only, and a vehicle sent to one is sold
       * (owner, 1 Oct 2026). "Sold vehicles" had read SOLD alone — only what a
       * dealer had sold on — beside an "All dispatches" that was the real sales
       * register; one entry now, and it is the register.
       */
      {
        to: "/stock?status=AT_DEALER&status=SOLD",
        label: "Sold vehicles",
        icon: <BadgeIndianRupee />,
        permission: "vehicles:read",
      },
      // The billing desk's register of what the order desk sent out — bills,
      // corrections, returns. It is the maker's alone (owner, 18 Sep 2026:
      // "remove dispatched from bright on wheels, it should be in autozone"),
      // and the office rail had never carried it.
      { to: "/dispatched", label: "Dispatched", icon: <PackageCheck />, permission: "requests:read", section: "/dispatched" },
      { to: "/returns", label: "Returns", icon: <Undo2 />, permission: "vehicles:read" },
      // A mitr hands a vehicle to another mitr; the approver answers here, in whichever book she sits (13 Oct 2026).
      { to: "/transfers", label: "Transfers", icon: <ArrowLeftRight />, permission: "vehicles:move" },
      // What the plant holds by the dozen — parts, not chassis (9 Sep 2026).
      { to: "/factory", label: "Factory store", icon: <Boxes />, permission: "factory:read", section: "/factory" },
      // Each production-store chassis painted first, then given its motor, controller and differential (26 Oct 2026).
      { to: "/paint-shop", label: "Paint shop", icon: <Paintbrush />, permission: "paint:write" },
      { to: "/production-store", label: "Chassis parts", icon: <Cog />, permission: "factory:write" },
      // The guards' gate register: loader boxes, frames and fitments between the two factories (23 Oct 2026).
      { to: "/material", label: "Material", icon: <Truck />, permission: "material:move" },
      // Everyone Autozone sells to — Bright on Wheels and FAB EV among them (1 Oct 2026).
      {
        to: "/dealers",
        label: "Dealers",
        icon: <Store />,
        permission: "parties:read",
      },
      {
        to: "/search",
        label: "Vehicle search",
        icon: <Search />,
        permission: "vehicles:read",
      },
    ],
  },
  {
    key: "intelligence",
    label: "Intelligence",
    items: [
      {
        to: "/analytics",
        label: "Analytics",
        icon: <Gauge />,
        permission: "analytics:read",
      },
      {
        to: "/alerts",
        label: "Alerts",
        icon: <Bell />,
        permission: "alerts:read",
      },
      {
        to: "/reports",
        label: "Reports",
        icon: <FileBarChart />,
        permission: "reports:run",
      },
    ],
  },
  // The maker is the master company (owner, 9 Sep 2026): the only desk. Stock
  // orders from every partner book land on the order desk; service calls and
  // complaints on the help desk.
  {
    key: "desk",
    label: "The desk",
    items: [
      {
        to: "/orders",
        label: "Order desk",
        icon: <ClipboardList />,
        permission: "requests:read",
        section: "/orders",
      },
      // Bright on Wheels' ledger, read from the maker's book (28 Sep 2026).
      { to: "/ledger", label: "Ledger", icon: <BookOpenText />, permission: "ledger:read", ledgerOnly: true },
      {
        to: "/production",
        label: "Production",
        icon: <Factory />,
        permission: "requests:read",
      },
      {
        to: "/desk",
        label: "Help desk",
        icon: <LifeBuoy />,
        permission: "requests:read",
      },
      // The service desk (20 Oct 2026): complaints, walk-ins and the parts fitted against them.
      { to: "/service-desk", label: "Service desk", icon: <Wrench />, permission: "service:desk" },
      // Batteries received at head office, by serial (23 Oct 2026).
      { to: "/ho-store", label: "HEAD OFFICE store", icon: <Battery />, permission: "service:desk" },
    ],
  },
  {
    key: "system",
    label: "System",
    items: [
      // Autozone's BUSY too (21 Oct 2026): its sales bills beside each dispatch, and the check.
      { to: "/busy-data", label: "BUSY", icon: <FileSpreadsheet />, permission: "busy:upload" },
      // Flyers and Yes/No questions to the counters (owner, 1 Oct 2026).
      { to: "/announcements", label: "Announcements", icon: <Megaphone />, permission: "announcements:manage" },
      {
        to: "/sync",
        label: "Sync",
        icon: <RefreshCw />,
        permission: "sync:read",
      },
      {
        to: "/admin/users",
        label: "Administration",
        icon: <Settings />,
        permission: "users:read",
        section: "/admin",
      },
    ],
  },
];

/**
 * The factory store keeper's rail (owner, 9 Sep 2026: "a new user for
 * factory stock — he will add all the products we have in the inventory in
 * the factory"). One screen, because that is the whole job.
 */
const FACTORY_NAV: NavGroup[] = [
  {
    key: "factory",
    label: "Factory",
    items: [
      { to: "/", label: "Factory store", icon: <Boxes />, permission: "factory:read", end: true, section: "/factory" },
      // The production store's chassis, to give each its motor, controller and differential (owner, 26 Oct
      // 2026: "they will only see production store chasis and they will select and assign").
      { to: "/production-store", label: "Production store", icon: <Cog />, permission: "factory:read" },
    ],
  },
];

/*
 * The painter's rail (owner, 26 Oct 2026: "painter will select chasis and
 * update the paint on the chasis"): the paint shop, and nothing else.
 */
const PAINTER_NAV: NavGroup[] = [
  {
    key: "paint",
    label: "Factory",
    items: [{ to: "/paint-shop", label: "Paint shop", icon: <Paintbrush />, permission: "paint:write", section: "/paint-shop" }],
  },
];

/*
 * The service desk's rail (owner, 20 Oct 2026: "arun he manages all complaints
 * and replacement of the company work on his portal"): his desk, and the
 * factory store the parts come from — the in & out register opens from there.
 */
const SERVICE_NAV: NavGroup[] = [
  {
    key: "service",
    label: "Service",
    items: [
      { to: "/service-desk", label: "Service desk", icon: <Wrench />, permission: "service:desk", section: "/service-desk" },
      { to: "/factory", label: "Factory store", icon: <Boxes />, permission: "factory:read", section: "/factory" },
      { to: "/ho-store", label: "HEAD OFFICE store", icon: <Battery />, permission: "service:desk", section: "/ho-store" },
    ],
  },
];

/**
 * The guards' rail (owner, 23 Oct 2026: "all 3 are guard in they will
 * transfer the material from here there"): the gate register, and nothing
 * else. Attendance, tasks and expenses join it from the Work group.
 */
/*
 * The guards' rail (owner, 24 Oct 2026: "In guard login only there will be 3
 * option in side bar attendence in and out", then "send and recieve will be
 * only for akhilesh rest neeraj store neeraj factory will just maintain in and
 * out of vehicles"). Attendance joins it from the Work group; nothing else does.
 */
function guardNav(movesMaterial: boolean): NavGroup[] {
  return [
    {
      key: "gate",
      label: "Gate",
      items: [
        ...(movesMaterial
          ? ([{ to: "/material", label: "Send / receive", icon: <Truck />, permission: "material:move" }] as NavItem[])
          : ([
            { to: "/gate/in", label: "Vehicle in", icon: <ArrowDownToLine />, permission: "gate:out" },
            { to: "/gate/out", label: "Vehicle out", icon: <ArrowUpFromLine />, permission: "gate:out" },
            // What the one sending guard sends to this gate, received here (24 Oct 2026: "the other guards will also recieve not send").
            { to: "/material", label: "Receive material", icon: <PackageCheck />, permission: "material:move" },
          ] as NavItem[])),
      ],
    },
  ];
}

/**
 * The telecalling team's rail (owner, 13 Oct 2026: "create 3 sales
 * telecalling team portal", then "they will see inquires which i will upload
 * in excel sheets … like a lead management system"): the owner's leads, which
 * are their home. Their attendance, tasks and expenses join it from the Work
 * group, as for every employee.
 */
const TELECALLER_NAV: NavGroup[] = [
  {
    key: "telecalling",
    label: "Telecalling",
    items: [{ to: "/leads", label: "Leads", icon: <PhoneCall />, permission: "vehicles:read" }],
  },
];

/**
 * The factory's PDI hand (owner, 25 Sep 2026: "create a new user amit factory
 * and he will also pdi store and vehicle and tranfer vehicle to dispatch
 * store"). The queue he works, the store he passes into, and a search to find
 * any vehicle — in the order a chassis goes through them. No order desk, no
 * dispatches: those stay with the office. Production (owner, 2 Oct 2026: "amit
 * works at factory he should know which vehicles to build from the orders")
 * is the orders read as what to build, without the desk itself.
 */
const FACTORY_PDI_NAV: NavGroup[] = [
  {
    key: "pdi",
    label: "Factory PDI",
    items: [
      { to: "/pdi", label: "Production store", icon: <ClipboardCheck />, permission: "vehicles:read" },
      { to: "/store", label: "HEAD OFFICE", icon: <Warehouse />, permission: "vehicles:read" },
      { to: "/production", label: "Production sheet", icon: <Factory />, permission: "production:read" },
      // The factory store (21 Oct 2026): he decides the counts the shelf and the books disagree on.
      { to: "/factory", label: "Factory store", icon: <Boxes />, permission: "factory:read", section: "/factory" },
      { to: "/search", label: "Vehicle search", icon: <Search />, permission: "vehicles:read" },
    ],
  },
];

/**
 * The TVS dispatch hand (owner, 6 Oct 2026: "Create sunil user tvs dispatch
 * guy move the complete dispatch section which already exist to his login").
 * The dispatch first — it is his home and his job — then the TVS stock it
 * empties, and a search to find any chassis. Signed in to the TVS book.
 */
const TVS_DISPATCH_NAV: NavGroup[] = [
  {
    key: "tvs-dispatch",
    label: "TVS dispatch",
    items: [
      // The orders coming in first — his day starts there (owner, 19 Oct 2026).
      { to: "/tvs-orders", label: "Orders", icon: <ClipboardList />, permission: "vehicles:move" },
      { to: "/tvs-dispatch", label: "Dispatch", icon: <Send />, permission: "vehicles:move" },
      // His store is the "Dispatch store" (owner, 19 Oct 2026: "production store name should be dispatch store") — his portal only.
      { to: "/stock", label: "Dispatch store", icon: <Warehouse />, permission: "vehicles:read" },
      { to: "/search", label: "Vehicle search", icon: <Search />, permission: "vehicles:read" },
    ],
  },
];

/**
 * The billing desk's rail (owner, 14 Sep 2026). One job, one screen: what has
 * gone out, and the bill against each of it. Deliberately no store, no order
 * desk and no dashboard — a billing clerk reads what was dispatched and raises
 * the invoice, and every other door is somebody else's.
 */
const BILLING_NAV: NavGroup[] = [
  {
    key: "billing",
    label: "Billing desk",
    items: [
      { to: "/", label: "Dispatched", icon: <PackageCheck />, permission: "requests:read", end: true, section: "/dispatched", wholesaleOnly: true },
      /*
       * In a distributor's book the desk is the billing section and nothing
       * else (owner, 21 Sep 2026: "she is not an admin anymore she is billing
       * person like neeraj she will only see billing section in her portal
       * thats it"). Billing first — it is the work and the home — then what
       * Autozone sent, which is the paper behind it. No search door: the desk
       * has its own search over the sales.
       */
      { to: "/billing", label: "Billing", icon: <ReceiptText />, permission: "billing:desk" },
      { to: "/ledger", label: "Ledger", icon: <BookOpenText />, permission: "ledger:read", ledgerOnly: true },
      { to: "/from-autozone", label: "Dispatch from Autozone", icon: <PackageCheck />, permission: "billing:retail", distributorOnly: true, partnerOnly: true },
      /*
       * The one more thing the billing desk does in the TVS book (owner, 23 Sep
       * 2026: "single login for her sandhya will have billing desk for bright
       * on wheels one for TVS and she will upload purchases from busy thats
       * it no other thing should be shown to her"): the switch to TVS shows
       * Billing and this, and nothing else.
       */
      /*
       * The TVS quick sale (owner, 23 Sep 2026: "for tvs sale a person or
       * sandhya can do sale by just pressing sale button … for now"). Gone
       * from the rail the moment the key is taken off the role.
       */
      { to: "/tvs-sale", label: "Sale", icon: <ShoppingCart />, permission: "vehicles:quick-sell", tvsOnly: true },
      // Every book's BUSY since 21 Oct 2026 — in TVS its first tab is that book's own door.
      { to: "/busy-data", label: "BUSY", icon: <FileSpreadsheet />, permission: "busy:upload" },
      { to: "/search", label: "Search", icon: <Search />, permission: "vehicles:read", wholesaleOnly: true },
    ],
  },
];

/**
 * The dispatch desk's rail (owner, 9 Sep 2026: "Rahul, the dispatch guy —
 * all the stock orders go to him and he processes them"): the orders, the
 * store they are filled from, what has gone out, the buyers.
 */
const DISPATCH_NAV: NavGroup[] = [
  {
    key: "desk",
    label: "Dispatch desk",
    items: [
      { to: "/", label: "Order desk", icon: <ClipboardList />, permission: "requests:read", end: true, section: "/orders" },
      /*
       * The bay's day now runs PDI → store → dispatch (owner, 16 Sep 2026:
       * "first he will select the chasis from factory store then mark them pdi
       * done then it will go to dispatch store then the dispatch will be
       * done"), so the rail is in that order and the store says which store it
       * is. Anything the factory has sent and nobody has checked is in PDI; the
       * store holds what may actually go out.
       */
      // "PDI store" in every rail that carries it — the owner's word for it (25 Sep 2026).
      { to: "/pdi", label: "Production store", icon: <ClipboardCheck />, permission: "vehicles:read" },
      { to: "/store", label: "HEAD OFFICE", icon: <Warehouse />, permission: "vehicles:read" },
      // What the orders ask for against the store (owner, 2 Oct 2026: "Make production section visible to amit and rahul").
      { to: "/production", label: "Production", icon: <Factory />, permission: "requests:read" },
      // The dispatched register, read the way the billing desk reads it (owner, 26 Oct 2026: "in vikas user he see
      // all dispatches but he should see dispatches like neeraj sees it") — every dispatch desk, in place of the
      // stock list of every vehicle ever sent. Billing and corrections stay the billing desk's keys.
      { to: "/dispatched", label: "Dispatched", icon: <PackageCheck />, permission: "requests:read", section: "/dispatched" },
      { to: "/dealers", label: "Dealers", icon: <Store />, permission: "parties:read" },
      { to: "/search", label: "Vehicle search", icon: <Search />, permission: "vehicles:read" },
    ],
  },
];

/**
 * The store desk's half of the rail — the part that is a DESK rather than a
 * counter (owner, 15 Sep 2026: "add dispatch desk and rights to vivek as well";
 * 16 Sep: "vivek dipatch isnt opening i think he as aceess issue").
 *
 * It was reusing DISPATCH_NAV whole, and two of those items do not resolve for
 * anyone but the maker's own dispatch account:
 *
 *   - "Order desk" points at `/`, which is the order desk ONLY for the DISPATCH
 *     role. A store desk is a counter role, so `/` is its own stock — the item
 *     labelled Dispatch desk landed the user back on their vehicles, which is
 *     exactly "dispatch isn't opening". Named explicitly here.
 *   - "Store" points at `/store`, which is Bright Autozone's own store and
 *     redirects anyone outside the wholesale book straight back to /stock. The
 *     store desk's store is its counter home, already the first item in its
 *     rail, so the duplicate is dropped rather than re-pointed.
 *
 * Vehicle search is left out too: the counter half of the rail already carries
 * it, and the same link twice in one sidebar reads as a mistake.
 */
const STORE_DESK_NAV: NavGroup[] = [
  {
    key: "desk",
    label: "Dispatch desk",
    items: [
      { to: "/orders", label: "Order desk", icon: <ClipboardList />, permission: "requests:read", section: "/orders" },
      {
        to: "/stock?status=AT_DEALER&status=SOLD",
        label: "All dispatches",
        icon: <Truck />,
        permission: "vehicles:read",
      },
      { to: "/dealers", label: "Dealers", icon: <Store />, permission: "parties:read" },
    ],
  },
];

const NAV: NavGroup[] = [
  {
    key: "overview",
    label: "Overview",
    items: [
      {
        to: "/",
        label: "Dashboard",
        icon: <LayoutDashboard />,
        permission: "analytics:read",
        end: true,
      },
      // The ASMs' month in the field, and the review and incentive (owner, 3 Oct 2026).
      { to: "/field-team", label: "Field team", icon: <Handshake />, permission: "field:review" },
      // Each ASM's mitrs and dealers, sale by sale (owner, 13 Oct 2026).
      { to: "/sales-performance", label: "ASM sales", icon: <TrendingUp />, permission: "analytics:read" },
    ],
  },
  {
    key: "stock",
    label: "Stock",
    items: [
      {
        to: "/stock",
        label: "Store",
        icon: <Warehouse />,
        permission: "vehicles:read",
      },
      /*
       * The TVS dispatch (owner, 24 Sep 2026: "Design a dispatch form for tvs
       * as well"): one vehicle off the TVS store to a mitr or an outlet, with
       * two plate photos. Under Stock, beside the store it empties, and on the
       * key that already moves TVS stock — so the billing desk (no
       * `vehicles:move`, the 23 Sep ruling) never sees it. TVS only; the other
       * books dispatch through the maker's order bay.
       */
      {
        to: "/tvs-dispatch",
        label: "Dispatch",
        icon: <Send />,
        permission: "vehicles:move",
        tvsOnly: true,
      },
      // The book's own dealers (owner, 27 Oct 2026: "yes give each book its own dealer page") —
      // from 18 Sep to 27 Oct dealers were Autozone's alone and this rail carried none.
      { to: "/dealers", label: "Dealers", icon: <Store />, permission: "parties:read" },
      {
        to: "/sub-dealers",
        label: "Mitrs",
        icon: <Truck />,
        permission: "parties:read",
      },
      {
        to: "/outlets",
        label: "Company outlets",
        icon: <Building2 />,
        permission: "parties:read",
      },
      // What has come back to us (owner, 11 Sep 2026: "every return of Bright
      // on Wheels should show in the Bright on Wheels store"). Only Autozone's
      // rail carried this; the list itself has always been per-book.
      { to: "/returns", label: "Returns", icon: <Undo2 />, permission: "vehicles:read" },
      // A mitr handing a vehicle to another mitr: the approval queue, or the book's list (13 Oct 2026).
      { to: "/transfers", label: "Transfers", icon: <ArrowLeftRight />, permission: "vehicles:move" },
    ],
  },
  /*
   * The desk the office works from (14 Sep 2026).
   *
   * Both of these lived in ASM_NAV, which is the area sales manager's rail —
   * and an ASM holds neither `requests:read` nor `billing:retail`, so they
   * rendered for nobody at all. "Dispatched" had been invisible since the owner
   * asked for it on 12 September, and Billing followed it into the same hole.
   * Each is gated on its own permission, so it appears for the desk that owns
   * it and for the office, and for nobody else.
   */
  {
    key: "desk",
    label: "Desk",
    items: [
      /*
       * Orders, for whoever may raise one.
       *
       * Gated on `requests:raise`, not on `requests:read` — reading the desk
       * and placing an order are different rights, and the people who place
       * them hold only the second. A territory-less ASM lost this the moment
       * he moved off the territory rail onto this one, which has no desk of
       * its own by design (9 Sep 2026): he could still raise an order through
       * the app but had no way to reach the screen, on a phone or a desktop
       * (owner, 15 Sep 2026: "the goswami portal is not showing orders
       * section and on mobile also it is not visible"). What he sees inside is
       * his own orders; the desk view stays with whoever holds `requests:read`.
       */
      {
        to: "/orders",
        label: "Orders",
        icon: <ClipboardList />,
        permission: "requests:raise",
        section: "/orders",
      },
      // The head ASMs' enquiry book — Manish and Goswami ride this rail (1 Oct 2026) — and the owner's whole book (13 Oct 2026).
      { to: "/enquiries", label: "Enquiries & meetings", icon: <UserPlus />, permission: "vehicles:read", enquiryBook: true },
      /*
       * The RECEIVING end of a dispatch, so it belongs to the book that
       * receives (owner, 16 Sep 2026: "dispatch from autozone shows the
       * vehicles autozone dispatched to bright on wheels or fab ev it should be
       * in only their books sidebar no asm nobody should have it").
       *
       * Two things had let it spread. It sat on `requests:read`, which is the
       * DESK's key and by now reaches a sales head and the dispatch bay; and
       * `distributorOnly` was honoured on the billing rail alone, so the flag
       * would have been inert here even if it had carried one. Both fixed: the
       * distributor billing key, and the flag now filtered on every rail.
       */
      {
        to: "/from-autozone",
        label: "Dispatch from Autozone",
        icon: <PackageCheck />,
        permission: "billing:retail",
        distributorOnly: true,
      },
      // The maker's register, read from this book for its own orders (owner,
      // 19 Sep 2026: "Dispatched is still missing from autozone, manish,
      // goswami"). Anyone who raises orders follows their vehicles here.
      {
        to: "/dispatched",
        label: "Dispatched",
        icon: <PackageCheck />,
        permission: "requests:raise",
        // Autozone's register: the TVS book buys from TVS, and the server refuses it there (9 Oct 2026).
        partnerOnly: true,
      },
      {
        to: "/billing",
        label: "Billing",
        icon: <ReceiptText />,
        permission: "billing:desk",
      },
      { to: "/ledger", label: "Ledger", icon: <BookOpenText />, permission: "ledger:read", ledgerOnly: true },
      // The TVS quick sale, for now (23 Sep 2026) — see BILLING_NAV.
      {
        to: "/tvs-sale",
        label: "Sale",
        icon: <ShoppingCart />,
        permission: "vehicles:quick-sell",
        tvsOnly: true,
      },
    ],
  },
  {
    key: "inventory",
    label: "Inventory",
    items: [
      {
        to: "/search",
        label: "Vehicle search",
        icon: <Search />,
        permission: "vehicles:read",
      },
      {
        to: "/ageing",
        label: "Ageing",
        icon: <Hourglass />,
        permission: "vehicles:read",
      },
    ],
  },
  {
    key: "intelligence",
    label: "Intelligence",
    items: [
      {
        to: "/sales",
        label: "Sales",
        icon: <TrendingUp />,
        permission: "analytics:read",
        wholeBook: true,
      },
      {
        /*
         * The sold book, vehicle by vehicle (owner, 16 Sep 2026: "sold vehicles
         * section on side bar which will show the sold vehicles along with
         * chasis number — sales section only shows performance no details").
         * "Sales" above it is the board of figures; this is the list underneath
         * them, with the chassis, the day, the customer and the amount.
         */
        to: "/stock?status=SOLD",
        label: "Sold vehicles",
        icon: <BadgeIndianRupee />,
        permission: "vehicles:read",
      },
      {
        to: "/analytics",
        label: "Analytics",
        icon: <Gauge />,
        permission: "analytics:read",
      },
      // No desk here: every request — orders, service, complaints — lands on
      // Bright Autozone's desk, the master company's (owner, 9 Sep 2026).
      {
        to: "/financiers",
        label: "Financiers",
        icon: <Landmark />,
        permission: "parties:read",
      },
      {
        to: "/alerts",
        label: "Alerts",
        icon: <Bell />,
        permission: "alerts:read",
      },
      {
        to: "/reports",
        label: "Reports",
        icon: <FileBarChart />,
        permission: "reports:run",
      },
    ],
  },
  {
    key: "system",
    label: "System",
    items: [
      // Every book's BUSY since 21 Oct 2026 (owner: "connect my crm … to my company busy anyhow").
      { to: "/busy-data", label: "BUSY", icon: <FileSpreadsheet />, permission: "busy:upload" },
      // Flyers and Yes/No questions to the counters (owner, 1 Oct 2026).
      { to: "/announcements", label: "Announcements", icon: <Megaphone />, permission: "announcements:manage" },
      {
        to: "/sync",
        label: "Sync",
        icon: <RefreshCw />,
        permission: "sync:read",
      },
      {
        to: "/admin/users",
        label: "Administration",
        icon: <Settings />,
        permission: "users:read",
        section: "/admin",
      },
    ],
  },
];

const COLLAPSE_KEY = "bsa.sidebar.collapsed";

/** How many alerts are waiting. Cheap: the list endpoint already reports the total. */
function useOpenAlertCount(enabled: boolean) {
  return useQuery({
    queryKey: ["alerts", "open-count"],
    queryFn: async () => {
      const result = await api.get<Paginated<unknown>>("/alerts", {
        status: "OPEN",
        pageSize: 1,
        page: 1,
      });
      return result.meta.total;
    },
    enabled,
    // The bell should be roughly right without becoming a polling load.
    refetchInterval: 120_000,
    staleTime: 60_000,
  });
}

/** The CEO's own frame (26 Oct 2026), in its own chunk with its face. */
const CeoShell = lazy(() => import("@/features/ceo/CeoShell"));

/**
 * The frame around every signed-in page. The CEO's desk has its own — the
 * Fernly dashboard's look the owner chose for it (26 Oct 2026) — and so do the
 * owner's desks while they are on his page, to see what he sees.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (isCeoDesk(user) || (readsCeoDesk(user) && (pathname === "/ceo" || pathname.startsWith("/ceo/")))) {
    return (
      <Suspense fallback={<div className="min-h-dvh bg-paper" />}>
        <CeoShell>{children}</CeoShell>
      </Suspense>
    );
  }
  // The store guard's own frame (5 Nov 2026): no rail, his name, home and sign out — in Hindi.
  if (user?.role.key === "STORE_GUARD") return <GuardShell>{children}</GuardShell>;
  return <StaffShell>{children}</StaffShell>;
}

/**
 * The store guard's frame (owner, 5 Nov 2026: "redesign his portal remove the sidebar and convert the app in hindi
 * for him"). A bar with the way home to his three tiles, and the page below it.
 */
function GuardShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  return (
    <div className="min-h-dvh bg-paper">
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-line bg-surface px-4 py-3 shadow-card">
        {pathname === "/guard" ? (
          <span className="font-display text-title font-semibold text-ink">{user?.name}</span>
        ) : (
          <Link to="/guard" className="flex h-12 items-center gap-2 rounded-control bg-surface-2 px-4 text-title font-semibold text-ink">
            <ArrowLeft className="h-5 w-5" aria-hidden /> होम
          </Link>
        )}
        <button type="button" onClick={() => void logout()} className="ml-auto flex h-12 items-center gap-2 rounded-control px-3 text-body text-ink-2 hover:bg-surface-2">
          <LogOut className="h-5 w-5" aria-hidden /> बाहर निकलें
        </button>
      </header>
      <main className="px-4 py-4">{children}</main>
    </div>
  );
}

function StaffShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  // The Enquiries & meetings desk and the office's field pages wear the Fernly look (27 Oct 2026, lib/fernly).
  useFernlyLook(wearsFernly(location.pathname));
  const navigate = useNavigate();
  const { user, has, logout, switchDesk } = useAuth();
  const toast = useToast();
  const { mode, theme, setMode } = useTheme();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  const alertCount = useOpenAlertCount(has("alerts:read"));
  const openAlerts = alertCount.data ?? 0;

  /*
   * Navigating on a phone should put the drawer away — and "navigating" has to
   * mean any change, not just a change of path. Several rail items differ only
   * by their query ("All dispatches" is /stock?status=…), so tapping one from
   * /stock left the drawer sitting open over the page it had just loaded and
   * the counter had to find the X (audit, 16 Sep 2026).
   */
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        // Preference will not persist. Harmless.
      }
      return next;
    });
  }, []);

  const { wholesale } = useActiveOrg();
  // The maker's book wears its own palette; every other book is the base one.
  useBookIdentity();
  const railBook = useActiveBook();
  // The work of someone this session covers (20 Oct 2026), in their book — see lib/cover.
  const railCover = coverInUse(user, railBook);
  const baseGroups = useMemo(() => {
    const railFor = (user: AuthUser | null): NavGroup[] => {
      // The maker's register belongs to a sign-in of the maker's book whichever
      // book the switch is showing — the admin flipping books must not lose it.
      const homeWholesale = Boolean(user?.organization?.wholesale);
      // A buyer portal account gets its own short rail — its home and a search of
      // its own vehicles — rather than the operations nav filtered down to a couple
      // of oddly-labelled items ("Bright on Wheels" is not a buyer's word for its
      // own stock).
      /*
       * The store desk gets the counter's rail plus the desk's.
       *
       * It is a counter — its stock is the store's own, it sells over that
       * counter, it keeps the enquiry book — and it is also what sends stock on
       * to the book's mitras and outlets, which is the order desk and the
       * dispatch form (owner, 15 Sep 2026).
       */
      if (isStoreDesk(user?.role.key)) {
        const counter = BUYER_NAV
          // Same as any store counter: it asks the office for nothing.
          .filter((group) => group.key !== "requests")
          .map((group) => ({ ...group, items: group.items.filter((item) => has(item.permission)) }))
          .filter((group) => group.items.length > 0);
        const desk = STORE_DESK_NAV.map((group) => ({
          ...group,
          items: group.items.filter((item) => has(item.permission)),
        })).filter((group) => group.items.length > 0);
        return [...counter, ...desk];
      }
      if (user?.role.key === "BUYER") {
        // The store's own counter keeps the enquiry book; the 277 counters at a
        // mitr, an outlet or a dealer do not, and never see the entry.
        const atTheStore = user.scopeParty?.type === "DISTRIBUTOR";
        const inTvs = (railBook ?? bookKeyFor(user.organization?.id ?? user.organizationId)) === "tvs";
        // A counter's ledger is its own book's — Bright on Wheels and FAB EV keep one (30 Oct 2026).
        const inLedgerBook = ["bow", "fabev"].includes(railBook ?? bookKeyFor(user.organization?.id ?? user.organizationId) ?? "");
        return BUYER_NAV
          // Service and orders are the counters' lane. The store has nobody to
          // order from — it IS the store — and its sales desk has nothing to do
          // with either (owner, 15 Sep 2026).
          .filter((group) => !(atTheStore && group.key === "requests"))
          .map((group) => ({
            ...group,
            items: group.items.filter(
              (item) =>
                !(item.storeOnly && !atTheStore) &&
                !(item.tvsOnly && !inTvs) &&
                // The store owes the company nothing on its own sales; a counter's ledger is its own book's.
                !(item.ledgerOnly && (!inLedgerBook || atTheStore || !has(item.permission))),
            ),
          }))
          .filter((group) => group.items.length > 0);
      }
      // The territory rail is for someone who HAS a territory. Without one the
      // role still says what they may do — read the book, raise an order — but
      // "Your territory / My mitras" is the wrong shape for the whole book.
      if (user?.role.key === "ASM" && user.asmId) {
        return ASM_NAV.map((group) => ({
          ...group,
          items: group.items.filter((item) => has(item.permission)),
        }));
      }
      if (user?.role.key === "FACTORY") {
        return FACTORY_NAV;
      }
      if (user?.role.key === "FACTORY_PDI") {
        return FACTORY_PDI_NAV;
      }
      if (user?.role.key === "TVS_DISPATCH") {
        return TVS_DISPATCH_NAV;
      }
      if (user?.role.key === "SERVICE") {
        return SERVICE_NAV;
      }
      if (user?.role.key === "PAINTER") {
        return PAINTER_NAV;
      }
      // A guard's rail (24 Oct 2026): vehicles in and out — or, for the one guard switched on for it, the material.
      if (isGuardRole(user?.role.key)) {
        // The factory guard's swipe out passes PDI itself since 5 Nov 2026 — no separate page on his rail.
        return guardNav(Boolean(user?.movesMaterial)).map((group) => ({ ...group, items: group.items.filter((item) => has(item.permission)) }));
      }
      // Staff (14 Oct 2026) have no book rail at all: the Work group — their attendance, tasks and expenses — is the whole menu.
      if (isStaffRole(user?.role.key)) {
        return [];
      }
      if (isTelecaller(user?.role.key)) {
        return TELECALLER_NAV;
      }
      // The billing desk and the chain's other desks (1 Oct 2026) share one rail.
      if (BILLING_DESK_ROLES.includes(user?.role.key as RoleKey)) {
        const inTvs = (railBook ?? bookKeyFor(user?.organization?.id ?? user?.organizationId)) === "tvs";
        const onLedger = ["bow", "fabev", "autozone"].includes(railBook ?? bookKeyFor(user?.organization?.id ?? user?.organizationId) ?? "");
        return BILLING_NAV.map((group) => ({
          ...group,
          items: group.items.filter(
            (item) =>
              has(item.permission) &&
              !(wholesale && item.distributorOnly) &&
              !(!homeWholesale && item.wholesaleOnly) &&
              !(item.tvsOnly && !inTvs) &&
              !(item.partnerOnly && inTvs) &&
              !(item.ledgerOnly && !onLedger),
          ),
        }));
      }
      if (user?.role.key === "DISPATCH") {
        return DISPATCH_NAV.map((group) => ({
          ...group,
          items: group.items.filter((item) => has(item.permission)),
        }));
      }
      const rail = wholesale ? WHOLESALE_NAV : NAV;
      const territory = Boolean(user?.asmId);
      const tvs = (railBook ?? bookKeyFor(user?.organization?.id ?? user?.organizationId)) === "tvs";
      const onLedger = ["bow", "fabev", "autozone"].includes(railBook ?? bookKeyFor(user?.organization?.id ?? user?.organizationId) ?? "");
      return rail
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) =>
              has(item.permission) &&
              !(territory && item.wholeBook) &&
              !(wholesale && item.distributorOnly) && !(!homeWholesale && item.wholesaleOnly) &&
              !(item.tvsOnly && !tvs) &&
              !(item.partnerOnly && tvs) &&
              !(item.asmOnly && !isAsmRole(user?.role.key)) &&
              !(item.enquiryBook && !isAsmRole(user?.role.key) && !readsEveryEnquiry(user)) &&
              !(item.ledgerOnly && (!onLedger || territory)),
          ),
        }))
        .filter((group) => group.items.length > 0);
    };
    if (!railCover || !user) return railFor(user);
    // Reached only by the cover: the covered job's rail, and nothing of one's own here.
    if (railCover.crossBook) return railFor(actingAs(user, railCover));
    // Worked in anyway: one's own rail, and the covered work's screens one does not have, under its own heading.
    const own = railFor(user);
    const have = new Set(own.flatMap((group) => group.items.map((item) => item.to)));
    const extra = railFor(actingAs(user, railCover))
      .flatMap((group) => group.items)
      .filter((item, at, all) => !have.has(item.to) && all.findIndex((other) => other.to === item.to) === at);
    return extra.length > 0 ? [...own, { key: "cover", label: `Covering ${railCover.forUser.name}`, items: extra }] : own;
  }, [has, user, wholesale, railBook, railCover]);

  /*
   * Tasks (12 Oct 2026): the owner who gives them and every employee who does
   * them, whatever their rail and whichever book they sit in — the work is the
   * person's, not the book's. A counter's sign-in has none.
   */
  const taskRole = useTaskRole();
  const taskSummary = useTaskSummary();
  const taskCount = taskBadgeCount(taskSummary.data);
  // Counts the shelf and the books disagree on, waiting for the owner or Amit (21 Oct 2026).
  const factoryApprover = useApprovesFactory() && has("factory:read");
  const factoryWaiting = useApprovals(factoryApprover).data?.length ?? 0;
  // Expenses are every employee's (owner, 12 Oct 2026) — the ASMs' too, in the menu where everyone
  // looks (13 Oct 2026: "i opened awadesh portal couldnt see expense section"). An ASM's open on the
  // field desk's Expenses tab, where they have always been kept.
  // The owner's desks keep their own too, paid back by the office (owner, 4 Nov 2026).
  const keepsExpenses = Boolean(user) && claimsExpenses({ roleKey: user!.role.key, email: user!.email, seatOfId: user!.seatOf?.id });
  // Attendance is every employee's, and the owner's register (13 Oct 2026) — first in the group, since it is the first thing done in a day.
  const attendanceRole = useAttendanceRole();
  // The owner's leads (13 Oct 2026) sit in no book, so they ride the Work group in every one.
  const leadRole = useLeadRole();
  /*
   * Approvals (owner, 17 Oct 2026: "Create a dedicated approval section for
   * surbhi for all the approvals coming in and for everybody"): every sign-in
   * but a counter's, in every rail — what waits for one's answer reaches
   * across books, so it rides the Work group, not one book's rail.
   */
  const approvals = Boolean(user) && hasApprovalsSection(user);
  const approvalCount = useApprovalsSummary().data?.total ?? 0;
  // Company chat (20 Oct 2026): every employee but the drivers, and the owner's desks — whatever book they sit in.
  // Another desk of a person (20 Oct 2026) — seatOf — is left out: their chat is on the person's own sign-in.
  const chats = Boolean(user) && chatsInCompany({ roleKey: user!.role.key, email: user!.email, seatOfId: user!.seatOf?.id });
  const chatUnread = useChatUnread().data?.total ?? 0;
  // Mitras' and outlets' showroom checks (23 Oct 2026): read by the office and by the ASMs, whose counters they are.
  const readsShowrooms = useReadsShowrooms();
  const groups = useMemo<NavGroup[]>(() => {
    // A guard has three things and nothing else (24 Oct 2026): attendance first, then his gate.
    if (user && isGuardRole(user.role.key)) {
      const attendance: NavItem[] = attendanceRole ? [{ to: "/attendance", label: "Attendance", icon: <CalendarCheck />, permission: "vehicles:read" as PermissionKey }] : [];
      return baseGroups.map((group, index) => (index === 0 ? { ...group, items: [...attendance, ...group.items] } : group));
    }
    const work: NavItem[] = [];
    if (leadRole === "manager") work.push({ to: "/leads", label: "Leads", icon: <PhoneCall />, permission: "vehicles:read" as PermissionKey });
    if (attendanceRole) work.push({ to: "/attendance", label: attendanceRole === "manager" ? "Attendance" : "My attendance", icon: <CalendarCheck />, permission: "vehicles:read" as PermissionKey });
    if (approvals) work.push({ to: "/approvals", label: "Approvals", icon: <Inbox />, permission: "vehicles:read" as PermissionKey });
    // An ASM's meetings, one after another, each with the selfie and the place (owner, 23 Oct 2026).
    // An ASM's meetings live on Enquiries & meetings since 27 Oct 2026 (/meetings leads there).
    if (readsShowrooms) work.push({ to: "/showrooms", label: "Showroom checks", icon: <Store />, permission: "vehicles:read" as PermissionKey });
    // Mitras' and outlets' weekly stock photos (26 Oct 2026): read by the same people as the showroom checks.
    if (readsShowrooms) work.push({ to: "/stock-photo-register", label: "Stock photos", icon: <Camera />, permission: "vehicles:read" as PermissionKey });
    // The owner hands someone's work over while they are away (20 Oct 2026).
    if (isHeadAdminDesk(user)) work.push({ to: "/covers", label: "Covers", icon: <UserRoundCheck />, permission: "vehicles:read" as PermissionKey });
    // The owner's enquiries desk (14 Oct 2026): every person's enquiries across the books, read from any book.
    if (user && readsEveryEnquiry(user)) work.push({ to: "/enquiry-desk", label: "Enquiries desk", icon: <Headset />, permission: "vehicles:read" as PermissionKey });
    // Dealer onboarding (27 Oct 2026): the ASMs and the office bring a dealer to Sunny; his yes makes the LOI.
    if (user && worksDealerOnboarding({ email: user.email, roleKey: user.role.key, permissions: has("parties:write") ? ["parties:write"] : [], scopePartyId: user.scopeParty?.id ?? null })) {
      work.push({ to: "/dealer-onboarding", label: "Mitr / dealer onboarding", icon: <BadgeCheck />, permission: "vehicles:read" as PermissionKey });
    }
    // A book's purchase sheet against the app (29 Oct 2026), each book its own (30 Oct 2026).
    if (checkableBooks(user).length > 0) {
      work.push({ to: "/purchase-check", label: "Purchase check", icon: <FileSpreadsheet />, permission: "vehicles:read" as PermissionKey });
    }
    // The financier's money against each loan (27 Oct 2026): Surbhi's in Bright on Wheels.
    if (financeReceiptBooks(user).length > 0) work.push({ to: "/finance-receipts", label: "Financier payments", icon: <Landmark />, permission: "vehicles:read" as PermissionKey });
    // Every employee gives tasks as well as doing them since 20 Oct 2026 ("employee to employee task manager").
    // Chat and tasks are one place (owner, 3 Nov 2026: "merge the task manager and chat option"): Team. The board is a
    // button inside it; a sign-in with tasks but no chat keeps its Tasks entry.
    if (chats) work.push({ to: "/chat", label: "Team", icon: <MessagesSquare />, permission: "vehicles:read" as PermissionKey, section: "/chat" });
    else if (taskRole) work.push({ to: "/tasks", label: user && givesTasks({ roleKey: user.role.key, email: user.email, seatOfId: user.seatOf?.id }) ? "Tasks" : "My tasks", icon: <ListChecks />, permission: "vehicles:read" as PermissionKey });
    if (keepsExpenses) work.push({ to: "/expenses", label: "My expenses", icon: <ReceiptIndianRupee />, permission: "vehicles:read" as PermissionKey });
    // The office that answers expenses pays them back, with the screenshot (19 Oct 2026).
    if (has("field:review")) work.push({ to: "/reimburse", label: "Reimburse", icon: <Wallet />, permission: "field:review" as PermissionKey });
    // Expense reconciliation (19 Oct 2026): the bank statement against the expense sheet, kept per book — the owner's desks and whoever is switched on in Users.
    // The cash day book (21 Oct 2026): company cash paid out by head, kept by the same hands.
    if (user && keepsDayBook(user)) work.push({ to: "/day-book", label: "Day book", icon: <NotebookPen />, permission: "vehicles:read" as PermissionKey });
    // Part swaps (22 Oct 2026): the office's in Bright on Wheels, the service desk's at head office — the page reads the book.
    if (
      user &&
      seesSwaps({ ...user, roleKey: user.role.key, homeBook: user.organization?.id ?? user.organizationId, serviceDesk: has("service:desk"), scoped: Boolean(user.scopeParty) })
    )
      work.push({ to: "/swaps", label: "Swaps", icon: <BatteryCharging />, permission: "vehicles:read" as PermissionKey });
    if (user && reconcilesExpenses(user)) work.push({ to: "/expense-recon", label: "Reconciliation", icon: <Scale />, permission: "vehicles:read" as PermissionKey });
    return work.length > 0 ? [{ key: "work", label: "Work", items: work }, ...baseGroups] : baseGroups;
  }, [baseGroups, attendanceRole, approvals, taskRole, keepsExpenses, leadRole, user, has, chats, readsShowrooms]);

  const openPalette = useCallback(() => {
    window.dispatchEvent(new CustomEvent(COMMAND_PALETTE_EVENT));
  }, []);

  /*
   * The other line (8 Sep 2026). Bright on Wheels and its TVS business are
   * two books with the same mitrs and outlets; staff signed into either flip
   * between them here, and every screen re-reads from the chosen book. A
   * counter sign-in never flips — its home shows both lines itself.
   */
  const ownBook = bookKeyFor(user?.organization?.id ?? user?.organizationId);
  const activeBook = useActiveBook();
  const queryClient = useQueryClient();
  const books = booksFor(user);
  const bookSwitchAt = (placement: string) =>
    books.length > 0 ? (
      <div
        role="group"
        aria-label="Which book"
        className={clsx("items-center rounded-control border border-line bg-surface p-0.5", placement)}
      >
        {books.map((key) => {
          const current = (activeBook ?? ownBook) === key;
          return (
            <button
              key={key}
              type="button"
              aria-pressed={current}
              onClick={() => {
                setActiveBook(key === ownBook ? null : key);
                void queryClient.invalidateQueries();
                navigate("/");
              }}
              className={clsx(
                "rounded-[calc(var(--r-control)-2px)] px-2.5 py-1 text-caption font-medium transition-colors",
                current ? "bg-accent-soft text-accent" : "text-muted hover:text-ink",
              )}
            >
              {BOOK_LABEL[key]}
            </button>
          );
        })}
      </div>
    ) : null;
  // In the header from tablet width up; on a phone, at the top of the menu —
  // Sandhya's billing desk reaches TVS only through this switch, and a phone
  // must not be the one place it is missing (23 Sep 2026).
  const bookSwitch = bookSwitchAt("hidden sm:inline-flex");

  /*
   * One person's desks (owner, 20 Oct 2026: "merge them this is the same
   * person"): Vivek's store desk and his dispatch desk, one tap apart — no
   * second PIN. Each desk is its own sign-in in its own book; the switch moves
   * this device's session over and lands on that desk's home.
   */
  const desks = user?.desks ?? [];
  const [switchingDesk, setSwitchingDesk] = useState<string | null>(null);
  const deskSwitchAt = (placement: string) =>
    desks.length > 1 ? (
      <div
        role="group"
        aria-label="Which desk"
        className={clsx("items-center rounded-control border border-line bg-surface p-0.5", placement)}
      >
        {desks.map((desk) => (
          <button
            key={desk.id}
            type="button"
            aria-pressed={desk.current}
            title={`${desk.name} · ${desk.book}`}
            disabled={desk.current || switchingDesk !== null}
            onClick={() => {
              setSwitchingDesk(desk.id);
              switchDesk(desk.id)
                .then(() => navigate("/"))
                .catch(() => toast.error("Could not switch desks", "Check the connection and try again."))
                .finally(() => setSwitchingDesk(null));
            }}
            className={clsx(
              "rounded-[calc(var(--r-control)-2px)] px-2.5 py-1 text-caption font-medium transition-colors disabled:cursor-default",
              desk.current ? "bg-accent-soft text-accent" : "text-muted hover:text-ink",
              switchingDesk === desk.id && "opacity-60",
            )}
          >
            {desk.role}
          </button>
        ))}
      </div>
    ) : null;

  const initials = (user?.name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

  /*
   * The sidebar as Apna Showroom has it (owner, 3 Nov 2026: "make it same like we did in new dms system click to open
   * and search bar"): a search over every page at the top (Ctrl/⌘ K), and each section a heading that opens with a tap
   * — one at a time, the section holding the open page opened by itself. A closed section shows the sum of its counts,
   * so nothing waiting hides. The narrow icon rail stays as it was.
   */
  const onItem = (item: NavItem) =>
    item.section !== undefined
      ? location.pathname.startsWith(item.section)
      : item.end
        ? location.pathname === item.to
        : location.pathname === item.to || location.pathname.startsWith(`${item.to === "/" ? "" : item.to}/`);
  const itemCount = (item: NavItem) =>
    item.to === "/tasks" ? taskCount : item.to === "/factory" ? factoryWaiting : item.to === "/chat" ? chatUnread + (taskRole ? taskCount : 0) : item.to === "/approvals" ? approvalCount : item.to === "/alerts" ? openAlerts : 0;
  const hereGroup = groups.find((group) => group.items.some(onItem))?.key ?? null;
  const [openGroup, setOpenGroup] = useState<string | null>(hereGroup);
  useEffect(() => {
    if (hereGroup) setOpenGroup(hereGroup);
  }, [hereGroup]);
  const [navQuery, setNavQuery] = useState("");
  const navSearch = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k" && navSearch.current?.offsetParent) {
        event.preventDefault();
        navSearch.current.focus();
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, []);
  const navTerms = navQuery.toLowerCase().split(/\s+/).filter(Boolean);
  const navFound = navTerms.length
    ? groups.flatMap((group) => group.items.filter((item) => navTerms.every((term) => `${item.label} ${group.label}`.toLowerCase().includes(term))))
    : null;

  const navList = (variant: "rail" | "drawer") => {
    const showLabel = variant === "drawer" || !collapsed;
    const renderItem = (item: NavItem) => {

              const showLabel = variant === "drawer" || !collapsed;
              const isActive =
                item.section !== undefined
                  ? location.pathname.startsWith(item.section)
                  : undefined;

              const link = (
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive: routerActive }) =>
                    clsx(
                      "tap-safe group relative flex items-center gap-2.5 rounded-control px-2.5 py-2.5 text-body font-medium",
                      "transition-colors",
                      (isActive ?? routerActive)
                        ? "bg-accent-soft text-accent-strong"
                        : "text-ink-2 hover:bg-surface-2 hover:text-ink",
                      !showLabel && "justify-center px-0",
                    )
                  }
                >
                  {({ isActive: routerActive }) => (
                    <>
                      {/* The active marker is a stamped bar on the rail, not a fill. */}
                      <span
                        aria-hidden
                        className={clsx(
                          "absolute top-1/2 left-0 h-4 w-[3px] -translate-y-1/2 rounded-r-full bg-accent transition-opacity duration-150",
                          (isActive ?? routerActive)
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                      <span
                        aria-hidden
                        className="shrink-0 [&>svg]:h-4 [&>svg]:w-4"
                      >
                        {item.icon}
                      </span>
                      {showLabel ? (
                        <span className="truncate">{item.label}</span>
                      ) : null}
                      {item.to === "/tasks" && taskCount > 0 ? (
                        <span
                          className={clsx(
                            "font-data text-label tabular-nums",
                            showLabel ? "ml-auto text-accent" : "absolute top-1 right-1 text-accent",
                          )}
                          aria-label={taskRole === "assigner" ? `${taskCount} waiting for your check` : `${taskCount} need you`}
                        >
                          {taskCount > 99 ? "99+" : taskCount}
                        </span>
                      ) : null}
                      {item.to === "/factory" && factoryWaiting > 0 ? (
                        <span
                          className={clsx(
                            "font-data text-label tabular-nums",
                            showLabel ? "ml-auto text-accent" : "absolute top-1 right-1 text-accent",
                          )}
                          aria-label={`${factoryWaiting} count${factoryWaiting === 1 ? "" : "s"} waiting for your decision`}
                        >
                          {factoryWaiting > 99 ? "99+" : factoryWaiting}
                        </span>
                      ) : null}
                      {item.to === "/chat" && itemCount(item) > 0 ? (
                        <span
                          className={clsx(
                            "font-data text-label tabular-nums",
                            showLabel ? "ml-auto text-accent" : "absolute top-1 right-1 text-accent",
                          )}
                          aria-label={`${itemCount(item)} unread messages and tasks needing you`}
                        >
                          {itemCount(item) > 99 ? "99+" : itemCount(item)}
                        </span>
                      ) : null}
                      {item.to === "/approvals" && approvalCount > 0 ? (
                        <span
                          className={clsx(
                            "font-data text-label tabular-nums",
                            showLabel ? "ml-auto text-accent" : "absolute top-1 right-1 text-accent",
                          )}
                          aria-label={`${approvalCount} waiting for your answer`}
                        >
                          {approvalCount > 99 ? "99+" : approvalCount}
                        </span>
                      ) : null}
                      {item.to === "/alerts" && openAlerts > 0 ? (
                        <span
                          className={clsx(
                            "font-data text-label tabular-nums",
                            showLabel
                              ? "ml-auto text-signal"
                              : "absolute top-1 right-1 text-signal",
                          )}
                        >
                          {openAlerts > 99 ? "99+" : openAlerts}
                        </span>
                      ) : null}
                    </>
                  )}
                </NavLink>
              );

              return (
                <li key={item.to}>
                  {showLabel ? (
                    link
                  ) : (
                    <Tooltip content={item.label} placement="right" delay={200}>
                      {link}
                    </Tooltip>
                  )}
                </li>
              );
    };
    // The narrow rail: every icon, as before.
    if (!showLabel) {
      return (
        <nav className="flex flex-col gap-4 py-3" aria-label="Sections">
          {groups.map((group) => (
            <div key={group.key}>
              <div className="mx-3 mb-2 h-px bg-line" aria-hidden />
              <ul className="flex flex-col gap-0.5 px-2">{group.items.map(renderItem)}</ul>
            </div>
          ))}
        </nav>
      );
    }
    const search = (
      <div className="relative mx-2 mb-1">
        <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-faint" aria-hidden />
        <input
          ref={navSearch}
          type="search"
          value={navQuery}
          onChange={(event) => setNavQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && navFound?.[0]) {
              navigate(navFound[0].to);
              setNavQuery("");
              if (variant === "drawer") setDrawerOpen(false);
            }
            if (event.key === "Escape") setNavQuery("");
          }}
          placeholder="Search pages"
          aria-label="Search pages"
          className="h-9 w-full rounded-control border border-line bg-surface pr-12 pl-9 text-label text-ink placeholder:text-faint focus:border-accent focus:outline-none [&::-webkit-search-cancel-button]:hidden"
        />
        {navQuery ? (
          <button type="button" onClick={() => setNavQuery("")} aria-label="Clear search" className="absolute top-1/2 right-2 -translate-y-1/2 rounded-full p-1 text-faint hover:text-ink">
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : (
          <kbd className="pointer-events-none absolute top-1/2 right-2.5 hidden -translate-y-1/2 rounded border border-line px-1 font-data text-micro text-faint lg:block">
            {/Mac|iPhone|iPad/.test(navigator.platform) ? "⌘K" : "Ctrl K"}
          </kbd>
        )}
      </div>
    );
    if (navFound) {
      return (
        <nav className="flex flex-col gap-1 py-3" aria-label="Search results">
          {search}
          <ul className="flex flex-col gap-0.5 px-2" onClick={() => setNavQuery("")}>
            {navFound.map(renderItem)}
          </ul>
          {navFound.length === 0 ? <p className="px-3 py-2 text-caption text-muted">No page matches “{navQuery.trim()}”.</p> : null}
        </nav>
      );
    }
    // One section alone needs no heading to open.
    const single = groups.length === 1;
    return (
      <nav className="flex flex-col gap-1 py-3" aria-label="Sections">
        {search}
        {groups.map((group) => {
          const open = single || openGroup === group.key;
          const waiting = group.items.reduce((sum, item) => sum + itemCount(item), 0);
          return (
            <div key={group.key}>
              {single ? null : (
                <button
                  type="button"
                  onClick={() => setOpenGroup(open ? null : group.key)}
                  aria-expanded={open}
                  className={clsx(
                    "tap-safe flex w-full items-center gap-2 rounded-control px-3 py-2 text-left transition-colors hover:bg-surface-2",
                    hereGroup === group.key && !open ? "text-accent-strong" : open ? "text-ink" : "text-ink-2",
                  )}
                >
                  <span className="label flex-1 !text-inherit">{group.label}</span>
                  {!open && waiting > 0 ? (
                    <span className="font-data text-label tabular-nums text-accent" aria-label={`${waiting} waiting`}>
                      {waiting > 99 ? "99+" : waiting}
                    </span>
                  ) : null}
                  <ChevronDown className={clsx("h-4 w-4 shrink-0 transition-transform duration-200", open && "rotate-180")} aria-hidden />
                </button>
              )}
              <div className={clsx("grid transition-[grid-template-rows] duration-200", open ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}>
                <div className="overflow-hidden" inert={!open}>
                  <ul className="flex flex-col gap-0.5 px-2 pb-1">{group.items.map(renderItem)}</ul>
                </div>
              </div>
            </div>
          );
        })}
      </nav>
    );
  };

  return (
    <div className="min-h-dvh bg-paper">
      {/* Rail — fixed from 1024px up. */}
      <aside
        className={clsx(
          "fixed inset-y-0 left-0 z-40 hidden shrink-0 flex-col border-r border-line bg-paper lg:flex",
          "transition-[width] duration-200",
          "print-hide",
        )}
        style={{
          width: collapsed ? "var(--sidebar-w-collapsed)" : "var(--sidebar-w)",
        }}
      >
        <div
          className={clsx(
            "flex h-[var(--header-h)] shrink-0 items-center gap-2 border-b border-line px-3",
            collapsed && "justify-center px-0",
          )}
        >
          <Brand collapsed={collapsed} />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">{navList("rail")}</div>

        <div className="shrink-0 border-t border-line p-2">
          <Button
            variant="ghost"
            size="sm"
            fullWidth={!collapsed}
            iconOnly={collapsed}
            onClick={toggleCollapsed}
            aria-label={
              collapsed ? "Expand the sidebar" : "Collapse the sidebar"
            }
            className={clsx(!collapsed && "justify-start")}
            leftIcon={
              collapsed ? (
                <ChevronsRight className="h-4 w-4" aria-hidden />
              ) : (
                <ChevronsLeft className="h-4 w-4" aria-hidden />
              )
            }
          >
            {collapsed ? null : "Collapse"}
          </Button>
        </div>
      </aside>

      {/* Drawer — below 1024px. */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        side="left"
        width="sm"
        title="Bright Stock Analyzer"
        description={user?.name}
      >
        {books.length > 0 ? <div className="mb-3 px-1">{bookSwitchAt("inline-flex")}</div> : null}
        {desks.length > 1 ? <div className="mb-3 px-1">{deskSwitchAt("inline-flex")}</div> : null}
        {navList("drawer")}
      </Drawer>

      <div
        className={clsx(
          "flex min-h-dvh flex-col transition-[padding] duration-200",
          collapsed
            ? "lg:pl-[var(--sidebar-w-collapsed)]"
            : "lg:pl-[var(--sidebar-w)]",
        )}
      >
        {/* Instrument bar */}
        <UpdateBanner />
        <header className="sticky top-0 z-30 flex h-[var(--header-h)] shrink-0 items-center gap-2 border-b border-line bg-paper/90 px-3 backdrop-blur-sm sm:px-4 print-hide">
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            className="lg:hidden"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open the navigation"
          >
            <Menu className="h-4 w-4" aria-hidden />
          </Button>

          <div className="lg:hidden">
            <Brand collapsed />
          </div>

          {/* Global search — the fastest route to a chassis number. Not for a
              sign-in that reads no vehicles (staff, drivers): it would only answer "not allowed". */}
          {has("vehicles:read") ? (
          <button
            type="button"
            onClick={openPalette}
            className={clsx(
              "tap-safe group ml-auto flex h-9 items-center gap-2 rounded-control border border-line bg-surface px-3",
              "text-micro text-muted transition-colors duration-150 hover:border-line-strong hover:text-ink",
              "lg:ml-0 lg:w-72",
            )}
          >
            <Search className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="hidden lg:inline">
              Search chassis, VIN, invoice
            </span>
            <span className="sr-only lg:hidden">Search</span>
            <kbd className="ml-auto hidden shrink-0 rounded-[4px] border border-line bg-surface px-1 font-data text-label text-faint lg:inline">
              ⌘K
            </kbd>
          </button>
          ) : null}

          <div className="ml-auto flex items-center gap-1">
            <Popover
              align="end"
              width={180}
              trigger={({ ref, onClick, ...aria }) => (
                <Button
                  ref={ref}
                  variant="ghost"
                  size="sm"
                  iconOnly
                  onClick={onClick}
                  aria-label={`Theme: ${mode}`}
                  title="Theme"
                  {...aria}
                >
                  {theme === "dark" ? (
                    <Moon className="h-4 w-4" aria-hidden />
                  ) : (
                    <Sun className="h-4 w-4" aria-hidden />
                  )}
                </Button>
              )}
            >
              {(close) => (
                <div className="py-1">
                  <MenuLabel>Theme</MenuLabel>
                  <MenuItem
                    icon={<Sun />}
                    onClick={() => {
                      setMode("light");
                      close();
                    }}
                  >
                    Light{mode === "light" ? " ·" : ""}
                  </MenuItem>
                  <MenuItem
                    icon={<Moon />}
                    onClick={() => {
                      setMode("dark");
                      close();
                    }}
                  >
                    Dark{mode === "dark" ? " ·" : ""}
                  </MenuItem>
                  <MenuItem
                    icon={<Monitor />}
                    onClick={() => {
                      setMode("system");
                      close();
                    }}
                  >
                    Match system{mode === "system" ? " ·" : ""}
                  </MenuItem>
                </div>
              )}
            </Popover>

            {bookSwitch}
            {deskSwitchAt("hidden sm:inline-flex")}

            {has("alerts:read") ? (
              <button
                type="button"
                onClick={() => navigate("/alerts")}
                aria-label={
                  openAlerts > 0
                    ? `Alerts: ${openAlerts} open`
                    : "Alerts: none open"
                }
                className="tap-safe relative inline-flex h-9 w-9 items-center justify-center rounded-control text-ink-2 transition-colors hover:bg-surface-2 hover:text-ink"
              >
                <Bell className="h-4 w-4" aria-hidden />
                {openAlerts > 0 ? (
                  <span className="absolute top-1 right-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-signal px-1 font-data text-label font-semibold text-white tabular-nums">
                    {openAlerts > 99 ? "99+" : openAlerts}
                  </span>
                ) : null}
              </button>
            ) : null}

            <Popover
              align="end"
              width={230}
              trigger={({ ref, onClick, ...aria }) => (
                <button
                  ref={ref}
                  type="button"
                  onClick={onClick}
                  {...aria}
                  className="tap-safe flex h-9 items-center gap-2 rounded-control pr-1.5 pl-1 text-left transition-colors hover:bg-surface-2"
                >
                  <span
                    aria-hidden
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-soft font-data text-label font-semibold text-accent-strong"
                  >
                    {initials || "··"}
                  </span>
                  <span className="hidden min-w-0 flex-col leading-tight sm:flex">
                    <span className="truncate text-micro font-medium text-ink">
                      {user?.name ?? "Signed in"}
                    </span>
                    <span className="truncate text-label text-muted">
                      {user?.role?.name ?? ""}
                    </span>
                  </span>
                </button>
              )}
            >
              {(close) => (
                <div className="py-1">
                  <div className="px-3 pt-2 pb-2">
                    <p className="truncate text-micro font-medium text-ink">
                      {user?.name ?? "Signed in"}
                    </p>
                    <p className="truncate text-micro text-muted">
                      {user?.email}
                    </p>
                    {user?.role?.name ? (
                      <Badge tone="accent" size="sm" className="mt-1.5">
                        {user.role.name}
                      </Badge>
                    ) : null}
                  </div>
                  <MenuSeparator />
                  {has("settings:manage") ? (
                    <MenuItem
                      icon={<Settings />}
                      onClick={() => {
                        close();
                        navigate("/admin/settings");
                      }}
                    >
                      Settings
                    </MenuItem>
                  ) : null}
                  {/* Enrolling employees for approval, where switched on for this person (7 Oct 2026). */}
                  {user?.enrolsEmployees ? (
                    <MenuItem
                      icon={<UserPlus />}
                      onClick={() => {
                        close();
                        navigate("/enrol-employee");
                      }}
                    >
                      Enrol employee
                    </MenuItem>
                  ) : null}
                  {/* One's own Aadhaar and details (6 Oct 2026) — every employee, never a counter or a head-admin desk. */}
                  {user && isVerifiedEmployee({ roleKey: user.role.key, email: user.email, seatOfId: user.seatOf?.id }) ? (
                    <MenuItem
                      icon={<ShieldCheck />}
                      onClick={() => {
                        close();
                        navigate("/my-verification");
                      }}
                    >
                      My verification
                    </MenuItem>
                  ) : null}
                  {has("users:read") ? (
                    <MenuItem
                      icon={<Users />}
                      onClick={() => {
                        close();
                        navigate("/admin/users");
                      }}
                    >
                      Users
                    </MenuItem>
                  ) : null}
                  <MenuSeparator />
                  <MenuItem
                    icon={<LogOut />}
                    destructive
                    onClick={() => {
                      close();
                      void logout();
                    }}
                  >
                    Sign out
                  </MenuItem>
                </div>
              )}
            </Popover>
          </div>
        </header>

        <main className="min-w-0 flex-1 px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
          {/* One way back, above the content, on every screen that is inside
              something. See Backstep. */}
          <div className="mb-3 empty:mb-0">
            <Backstep />
          </div>
          <TasksNudge />
          <AttendanceNudge />
          <EnquiryNudge />
          <LeadsNudge />
          <VerificationNudge />
          {/* Everything waiting for this person's answer — transfers, orders, leave, expenses, new employees, Aadhaar checks — in one bar (17 Oct 2026). */}
          <ApprovalsNudge />
          <CoverNudge />
          {/* A transfer from another mitr waiting for this mitr's tick — on every page but home (13 Oct 2026). */}
          {user?.role.key === "BUYER" && transfersOpenTo(user.scopeParty?.type, user.organization?.id ?? user.organizationId) ? (
            <div className="mb-3 empty:mb-0">
              <IncomingTransferBanner enabled />
            </div>
          ) : null}
          {children}
        </main>
      </div>
    </div>
  );
}

function Brand({ collapsed }: { collapsed: boolean }) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span
        aria-hidden
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm bg-accent font-data text-micro font-bold text-accent-ink"
      >
        BW
      </span>
      {!collapsed ? (
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="truncate font-display text-micro font-semibold text-ink">
            Bright Stock
          </span>
          <span className="truncate text-label tracking-[0.08em] text-faint uppercase">
            Analyzer
          </span>
        </span>
      ) : null}
    </span>
  );
}
